<script setup lang="ts">
import { useFilteredGeojsonLayers } from "@/composables/geojsonConfig";

const props = defineProps({
  trekTitle: String,
  trekTitleShort: String,
  trekLocation: String,
  trekPage: Boolean,
  trekId: Number,
  inline: Boolean,
});

const root = ref<HTMLElement | null>(null);
const active = ref(false);
const startOpen = ref(false);
const mapReady = ref(false);
const layers = useFilteredGeojsonLayers(props.trekLocation);
const config = useRuntimeConfig();

const MapboxMain = defineAsyncComponent(
  () => import("./MapboxMain.vue"),
);

const DEFAULT_POSTER = "/images/globe.png";
const posterFailed = ref(false);

const preferredSrc = computed(() => {
  const token = config.public.mapboxAccessToken;
  const title = `${props.trekTitle ?? ""}`.trim().toLowerCase();
  const layer = title
    ? layers.value.find(
        (item) =>
          `${item.title} ${item.route ?? ""}`.trim().toLowerCase() === title,
      )
    : null;

  if (layer?.poster) {
    return layer.poster;
  }

  if (layer?.center && token) {
    const [lon, lat] = layer.center;
    const zoom = Number(layer.initialZoom ?? layer.zoom ?? 8);
    const z = Math.min(22, Math.max(0, zoom)).toFixed(2);
    return `https://api.mapbox.com/styles/v1/mapbox/satellite-v9/static/${lon},${lat},${z},0/640x400?access_token=${encodeURIComponent(token)}`;
  }

  return DEFAULT_POSTER;
});

const previewSrc = computed(() =>
  posterFailed.value ? DEFAULT_POSTER : preferredSrc.value,
);

watch(preferredSrc, () => {
  posterFailed.value = false;
});

function onPosterError() {
  if (previewSrc.value !== DEFAULT_POSTER) {
    posterFailed.value = true;
  }
}

const previewAlt = computed(() =>
  props.trekTitle
    ? `Map of ${props.trekTitle}`
    : "View all our trekking routes on one map",
);

const previewHeading = computed(() => {
  if (props.trekLocation === "Worldwide") {
    return "Our Worldwide Trekking Routes";
  }
  if (props.trekLocation === "Europe") {
    return "Our European Trekking Routes";
  }
  return "All our Trekking Routes";
});

let observer: IntersectionObserver | null = null;

/**
 * Mount MapboxMain. Non-trek pages (homepage) can mount without opening
 * the modal so the globe + Explore overlay is visible. Trek pages / explicit
 * clicks set startOpen so the fullscreen map opens.
 */
function reveal(open: boolean) {
  if (open) startOpen.value = true;
  active.value = true;
  observer?.disconnect();
  observer = null;
}

function onClosed() {
  // Trek pages return to the static poster. Homepage keeps MapboxMain so
  // the globe overlay text stays after closing the modal.
  if (props.trekPage) {
    active.value = false;
    startOpen.value = false;
    mapReady.value = false;
  } else {
    startOpen.value = false;
  }
}

onMounted(() => {
  // Homepage / listing maps: mount MapboxMain when scrolled into view so
  // the overlaid Explore text appears on the globe (without opening modal).
  if (props.trekPage) return;

  const node = root.value;
  if (!node || active.value || typeof IntersectionObserver === "undefined") {
    return;
  }

  observer = new IntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) {
      reveal(false);
    }
  });
  observer.observe(node);
});

onUnmounted(() => {
  observer?.disconnect();
});
</script>

<template>
  <div ref="root" class="mapbox-shell">
    <MapboxMain
      v-if="active"
      :trek-title="trekTitle"
      :trek-title-short="trekTitleShort"
      :trek-location="trekLocation"
      :trek-page="trekPage"
      :trek-id="trekId"
      :inline="inline"
      :start-open="startOpen"
      @ready="mapReady = true"
      @close="onClosed"
    />
    <button
      v-if="!active || (trekPage && !mapReady)"
      type="button"
      class="mapbox-poster"
      :class="{
        'mapbox-poster--trek': trekPage,
        'mapbox-poster--home': !trekPage,
        'is-pending': active && trekPage,
      }"
      @click="reveal(true)"
    >
      <img
        :src="previewSrc"
        :alt="previewAlt"
        width="640"
        height="400"
        decoding="async"
        loading="lazy"
        @error="onPosterError"
      />
      <template v-if="!trekPage">
        <span class="mapbox-poster__scrim" aria-hidden="true" />
        <span class="mapbox-poster__copy">
          <AppSVGRegionsIcon />
          <h3>Explore</h3>
          <h2>{{ previewHeading }}</h2>
          <h3>On One Map</h3>
          <span class="btn from-left">{{
            active ? "Loading map…" : "Go"
          }}</span>
        </span>
      </template>
      <span v-else>{{ active ? "Loading map…" : "Click to open map" }}</span>
    </button>
  </div>
</template>

<style scoped>
.mapbox-shell {
  position: relative;
}

.mapbox-shell:has(.is-pending) {
  min-height: 50vh;
}

@media (min-width: 992px) {
  .mapbox-shell:has(.is-pending) {
    min-height: 45vw;
  }
}

.mapbox-poster.is-pending {
  position: absolute;
  inset: 0;
  z-index: 2;
  height: 100%;
  min-height: 0;
}

.mapbox-poster.is-pending img {
  height: 100%;
}

.mapbox-poster {
  position: relative;
  display: block;
  width: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  background: #000;
  cursor: pointer;
  min-height: 60vh;
}

.mapbox-poster img {
  display: block;
  width: 100%;
  height: 60vh;
  object-fit: cover;
}

.mapbox-poster > span:not([class]) {
  position: absolute;
  left: 50%;
  bottom: 20px;
  transform: translateX(-50%);
  color: #fff;
  background: #f15822;
  padding: 10px 20px 12px;
  line-height: 1.2;
  font-weight: 700;
  white-space: nowrap;
}

.mapbox-poster--home {
  min-height: 100vh;
  background: #003149;
}

.mapbox-poster--home img {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 60%;
  height: auto;
  max-height: none;
  transform: translate(-50%, -50%);
  object-fit: contain;
}

@media (max-width: 991px) {
  .mapbox-poster--home {
    min-height: 60vh;
  }

  .mapbox-poster--home img {
    width: 100%;
  }
}

.mapbox-poster__scrim {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1;
}

.mapbox-poster__copy {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: #fff;
  text-transform: uppercase;
  padding: 1rem;
}

.mapbox-poster__copy :deep(.regions-icon) {
  width: 100px;
  margin: 0 auto 20px;
}

.mapbox-poster__copy :deep(.regions-icon svg) {
  fill: #fff;
}

.mapbox-poster__copy h2,
.mapbox-poster__copy h3 {
  margin: 0;
  color: #fff;
}

.mapbox-poster__copy h3 {
  font-size: 200%;
}

.mapbox-poster__copy .btn {
  margin-top: 20px;
  color: #000;
  pointer-events: none;
}

@media (max-width: 991px) {
  .mapbox-poster__copy {
    width: 90%;
    margin: 0 auto;
  }

  .mapbox-poster__copy h2 {
    font-size: 180%;
    margin-bottom: 3px;
  }

  .mapbox-poster__copy h3 {
    font-size: 100%;
  }
}

.mapbox-poster--trek,
.mapbox-poster--trek img {
  min-height: 50vh;
  height: 50vh;
}

@media (min-width: 992px) {
  .mapbox-poster--trek,
  .mapbox-poster--trek img {
    height: 45vw;
    min-height: 0;
  }
}
</style>
